import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react";
import { IMAGES } from "@/assets/images";
import { useLang } from "@/i18n";

const ease = [0.4, 0, 0.2, 1];

export default function Services() {
  const { t } = useLang();
  const [active, setActive] = useState(0);
  const current = t.services.items[active];
  const tabIcons = [
    "mdi:account-star-outline",
    "mdi:map-marker-star-outline",
    "mdi:newspaper-variant-outline",
    "mdi:chart-areaspline",
  ];

  return (
    <section id="services" style={{ background: "#050505", overflow: "hidden" }}>

      {/* ── Full-width image break ── */}
      <div style={{ position: "relative", height: "45vh", minHeight: 280, overflow: "hidden" }}>
        <AnimatePresence mode="sync">
          <motion.img
            key={active}
            src={[IMAGES.SERVICES_DARK_4, IMAGES.SERVICES_DARK_1, IMAGES.SERVICES_DARK_2, IMAGES.SERVICES_DARK_5, IMAGES.SERVICES_DARK_6][active % 5]}
            alt=""
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease }}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 35%" }}
          />
        </AnimatePresence>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(5,5,5,0.95) 0%, rgba(5,5,5,0.6) 50%, rgba(5,5,5,0.2) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(5,5,5,0.4) 0%, transparent 40%, rgba(5,5,5,0.95) 100%)" }} />

        {/* Overlay heading */}
        <div className="howhero-head-left" style={{ position: "absolute", bottom: "2rem", left: "4rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
            <div style={{ width: 28, height: 1.5, background: "#f4440c" }} />
            <span style={{ fontSize: "0.7rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "#f4440c", fontWeight: 400 }}>{t.services.eyebrow}</span>
          </div>
          <h2 style={{
            fontFamily: "'Inter Tight', sans-serif",
            fontSize: "clamp(2.4rem, 4.5vw, 4rem)",
            fontWeight: 700,
            color: "#fff",
            lineHeight: 1.0,
            letterSpacing: "-0.04em",
          }}>
            {t.services.headline1}<br />
            <span style={{ color: "transparent", WebkitTextStroke: "1.5px #f4440c" }}>{t.services.headline2}</span>
          </h2>
        </div>
      </div>

      {/* ── Content area ── */}
      {/* Subtitle */}
      <div className="max-w-7xl mx-auto services-subtitle-pad">
        <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.38)", fontWeight: 300, letterSpacing: "0.03em" }}>
          {t.services.subtitle}
        </p>
      </div>

      <div className="max-w-7xl mx-auto services-pad">

        {/* Tab list */}
        <div style={{
          display: "flex",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          marginBottom: "4rem",
          gap: "0",
          overflowX: "auto",
        }}>
          {t.services.tabs.map((tabLabel, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              style={{
                flex: "0 0 auto",
                background: "none",
                border: "none",
                borderBottom: active === i ? "2px solid #f4440c" : "2px solid transparent",
                marginBottom: -1,
                cursor: "pointer",
                padding: "1.5rem 1.2rem 1.3rem",
                color: active === i ? "#fff" : "rgba(255,255,255,0.32)",
                fontSize: "0.85rem",
                letterSpacing: "0.04em",
                fontWeight: active === i ? 600 : 400,
                transition: "all 0.3s",
                textAlign: "center",
                minWidth: 72,
              }}
              onMouseEnter={e => { if (active !== i) (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.65)"; }}
              onMouseLeave={e => { if (active !== i) (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.32)"; }}
            >
              <Icon icon={tabIcons[i]} style={{ display: "block", margin: "0 auto 0.6rem", fontSize: 24, color: active === i ? "#f4440c" : "inherit" }} />
              {tabLabel}
            </button>
          ))}
        </div>

        {/* Content panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease }}
            className="services-content-grid" style={{ alignItems: "start" }}
          >
            {/* Left */}
            <div>
              <div style={{
                fontSize: "0.72rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#f4440c",
                fontWeight: 400,
                marginBottom: "1.5rem",
              }}>
                {current.subtitle}
              </div>
              <h3 style={{
                fontFamily: "'Inter Tight', sans-serif",
                fontSize: "clamp(1.8rem, 2.8vw, 2.6rem)",
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1.0,
                letterSpacing: "-0.04em",
                marginBottom: "1.8rem",
              }}>
                {current.title.split(" / ")[0]}<br />
                <span style={{ color: "transparent", WebkitTextStroke: "1px #f4440c", fontSize: "0.85em" }}>{current.title.split(" / ")[1] ?? ""}</span>
              </h3>
              <p style={{
                fontSize: "1rem",
                lineHeight: 1.95,
                color: "rgba(255,255,255,0.58)",
                fontWeight: 300,
                borderLeft: "2px solid rgba(244,68,12,0.3)",
                paddingLeft: "1.5rem",
              }}>
                {current.description}
              </p>

              {/* CTA */}
              <motion.button
                whileHover={{ x: 6 }}
                transition={{ duration: 0.2 }}
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                style={{
                  marginTop: "2.5rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "#f4440c",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                {t.services.inquire}
                <Icon icon="mdi:arrow-right" style={{ fontSize: 16 }} />
              </motion.button>
            </div>

            {/* Right: items */}
            <div>
              <div style={{
                fontSize: "0.68rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.28)",
                marginBottom: "1.5rem",
                paddingBottom: "1rem",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}>
                {t.services.itemsLabel}
              </div>
              {current.items.map((item: string, i: number) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.4, ease }}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1.25rem",
                    padding: "1.1rem 0",
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                  }}
                >
                  <span style={{
                    fontFamily: "'Inter Tight', sans-serif",
                    fontSize: "0.85rem",
                    color: "rgba(244,68,12,0.5)",
                    fontWeight: 400,
                    letterSpacing: "0.06em",
                    paddingTop: "0.1rem",
                    flexShrink: 0,
                    minWidth: 24,
                  }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span style={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.62)", lineHeight: 1.85, fontWeight: 300 }}>
                    {item}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
